import { Component, Provide, Vue, Watch } from 'vue-property-decorator';
import { Employee, IEmployee } from '@/shared/model/employee.model';
import { IPersonalPin, PersonalPin } from '@/shared/model/personal-pin.model';
import EmployeeService from '@/entities/employee/employee.service';
import EmployeeImageService from './employee-image.service';
import VuePinCodeInput from '@/account/spe/vue-pin-code-input/vue-pin-code-input.vue';
import EmployeeSearchComponent from '@/account/spe/employee-authentication/employee-search.vue';
import PersonalPinExtendedService from '@/account/spe/employee-authentication/personal-pin-extended.service';
import { AxiosError } from 'axios';
import axios from 'axios';

@Component({
  components: {
    'employee-pin': VuePinCodeInput,
    'employee-search': EmployeeSearchComponent,
  },
})
export default class EmployeeAuthentication extends Vue {
  @Provide('employeeService')
  private employeeService = () => new EmployeeService();
  @Provide('personalPinExtendedService')
  private personalPinExtendedService = () => new PersonalPinExtendedService();

  @Provide('employeeImageService')
  private employeeImageService = () => new EmployeeImageService();

  private employee: IEmployee = new Employee();

  private personalPin: IPersonalPin = new PersonalPin();

  private alert = false;

  private alertMessage: string | null = null;
  private currentLanguage = '';

  private get isEmployeeSet(): boolean {
    return this.employee && (this.employee.number ?? 0) !== 0;
  }
  onSearchError(): void {
    this.alertMessage = `${this.$t('error.server.not.reachable')}`;
    this.alert = true;
  }

  onPinComplete(pin: string): void {
    this.password = pin;
    this.login();
  }

  private password: string | null = null;

  @Watch('onSelectedEmployee')
  onSelectedEmployee(val: string): void {
    if (val) {
      const params: { [k: string]: any } = {
        filter: `number-is-${this.employee.number}`,
      };

      this.personalPinExtendedService()
        .findBy(params)
        .then(res => {
          this.personalPin = res;
        })
        .catch(error => {
          if (error instanceof AxiosError && error.response && error.response.status === 404) {
            this.personalPin = null;
          } else {
            this.alert = true;
            this.alertMessage = `${this.$t('spe.marutApp.employee.retrievePersonalPinError')}`;
          }
        });
    }
  }

  async login() {
    if ((this.password ?? '') === '') return;
    if (!this.isEmployeeSet) return;

    const userID = this.employee.number;

    let loggedIn = false;

    // catch network error
    try {
      // eslint-disable-next-line
      loggedIn = await this.personalPinExtendedService().login(`${userID}`, this.password!);
    } catch (error) {
      console.error(error);
      this.alertMessage = `${this.$t('error.server.not.reachable')}`;
      this.alert = true;
      return;
    }

    // catch wrong login
    if (!loggedIn) {
      // alert
      this.alertMessage = `${this.$t('error.authentication')}`;
      this.alert = true;
      return;
    }

    // add employee data to vuex
    try {
      const employee = await this.employeeService().find(`${this.employee.number}`);
      // The /active/ endpoint returns a lighter response without image — fetch it separately
      try {
        const image = await this.employeeImageService().find(`${this.employee.number}`);
        if (image && image.image) {
          employee.image = { ...image, image: `data:image/png;base64,${image.image}` };
        }
      } catch {
        // Image not available — continue without it
      }
      if (employee.admissionDate) {
        employee.admissionDate = new Date(employee.admissionDate);
      }
      if (employee.birthDate) {
        employee.birthDate = new Date(employee.birthDate);
      }
      if (employee.citizenCardExpireDate) {
        employee.citizenCardExpireDate = new Date(employee.citizenCardExpireDate);
      }

      this.$store.dispatch('setEmployee', employee);
    } catch (error) {
      this.alertMessage = `${this.$t('error.server.not.reachable')}`;
      this.alert = true;
      return;
    }

    // Mint a JWT for authenticated Tejas API calls (e.g. event subscriptions)
    try {
      const tokenRes = await axios.post('/api/v1/employee-auth-tokens', {
        number: userID,
        password: this.password,
        audience: 'panther',
        intent: 'marut-session',
      });
      this.$store.dispatch('setEmployeeAuthToken', tokenRes.data.token);
    } catch {
      // Token minting is best-effort — continue without it
      this.$store.dispatch('setEmployeeAuthToken', null);
    }

    // redirect employee to absence form
    this.$router.push({ name: 'EmployeeAbsenceCreate' });
  }

}
